// Panel inline para subir productos nuevos. Tiene DOS estados en una sola
// pantalla (réplica del "agregar producto" del estudio clásico):
//   • Sin fotos (estado explicativo): nombre opcional + card "Imágenes" con 2
//     fotos de ejemplo (según rubro) + tips de cómo subir + botón
//     "Subir imagen del producto" (abre el file picker).
//   • Con fotos: grilla de previews (+ "Agregar"), nombre y "Confirmar".
//
// El upload real (Supabase + naming IA) corre afuera; este panel solo expone
// callbacks. Dos variants visuales:
//   • desktop → header / scroll / footer sticky (px-6 interno). Vive como vista
//     de un side panel.
//   • mobile  → flex-col simple; el host le da padding externo.
//
// Consumers: PresetDetailSheet (Choose → Upload flow) y
// MultiSlotProductPickerSheet.

import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Layers, Plus, X } from 'lucide-react';
import { AbanicoDeFotos, TarjetaDeAgregarFoto, abrirConTeclado } from '../../../components/AbanicoDeFotos';
import { ZonaDeSubida } from './ZonaDeSubida';
import {
  AsideDeAlta,
  BotonPrincipalDeAlta,
  BotonSecundarioDeAlta,
  ColumnaDeTrabajo,
  ColumnasDeAlta,
  PieDeAlta,
  TipsDeAlta,
} from './altaEnDosColumnas';

export interface UploadProductPanelProps {
  variant: 'mobile' | 'desktop';
  files: File[];
  name: string;
  onNameChange: (v: string) => void;
  onAddMore: (files: FileList | File[]) => void;
  onRemoveFile: (idx: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
  uploading: boolean;
  error: string | null;
  /** Rubro del usuario — define las fotos de ejemplo del estado vacío
   *  (Moda → bolso, Hogar → sillón, resto → botella) y si se muestra el tip
   *  de "producto siendo utilizado". */
  rubro?: string | null;
  /** Si se provee, el estado vacío muestra además el botón "Subir varios
   *  productos". Ya NO abre el file picker directo: abre el popup de alta en
   *  bloque de Mi Marca (nombres editables, Tiendanube, tope por plan), que
   *  monta el host. Antes era exclusivo de Catálogo; ahora va en todos los
   *  modos, con el mismo popup que el resto de las pantallas (ADR-684). */
  onBulkUpload?: () => void;
  /** Oculta el link "Volver" interno (cuando el host ya da un back, p.ej.
   *  la top bar mobile en la pantalla full-screen). */
  hideBack?: boolean;
  /**
   * El alta ocupa una ventana ANCHA (el selector de fuentes de la caja del Agente, 980):
   * la foto y el nombre a la izquierda, el ejemplo y los tips a la derecha, y el CTA en
   * una barra de borde a borde — ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-
   * columnas. El título lo pone el host (la cabecera del alta), así que acá no se repite.
   * Sin esto, el panel queda exactamente como estaba para el drawer del Catálogo y el
   * panel de Modelo v2, que miden ~420.
   */
  ancho?: boolean;
  /**
   * Sin el título propio: el host ya pone el encabezado ("Agregar producto" + su X), como
   * el drawer de los modos. Sin esto se leían dos títulos seguidos.
   */
  sinEncabezado?: boolean;
}

/** Extensiones de foto que se aceptan al soltar un archivo que llega sin `type`. */
const EXTENSIONES_DE_FOTO = /\.(jpe?g|png|webp|avif|heic|heif)$/i;
const esFoto = (f: File) =>
  f.type ? f.type.startsWith('image/') : EXTENSIONES_DE_FOTO.test(f.name);

// Fotos de ejemplo por rubro (mismas que el "agregar producto" clásico).
function getExampleImages(rubro?: string | null) {
  if (rubro === 'Moda e Indumentaria') {
    return {
      img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg',
      img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png',
    };
  }
  if (rubro === 'Hogar y Decoración') {
    return {
      img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png',
      img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png',
    };
  }
  return {
    img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png',
    img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png',
  };
}

export const UploadProductPanel: React.FC<UploadProductPanelProps> = ({
  variant,
  files,
  name,
  onNameChange,
  onAddMore,
  onRemoveFile,
  onCancel,
  onConfirm,
  uploading,
  error,
  rubro,
  onBulkUpload,
  hideBack = false,
  ancho = false,
  sinEncabezado = false,
}) => {
  const addMoreRef = useRef<HTMLInputElement | null>(null);
  const inputId = `subir-producto-${useId()}`;
  const [arrastrando, setArrastrando] = useState(false);
  const hasFiles = files.length > 0;
  const isFashion = rubro === 'Moda e Indumentaria';
  const examples = getExampleImages(rubro);
  // En moda el tile "+" sugiere qué otra foto sumar (detalle / espalda de la
  // prenda); en el resto de los rubros alcanza con "Agregar".
  const addTileLabel = isFashion ? 'Agregar detalle o parte de atrás' : 'Agregar';

  // Previews: para cada File, generamos una URL local (createObjectURL)
  // y la liberamos al desmontar/cambiar files para no leakear memoria.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  const isDesktop = variant === 'desktop';

  // Input file compartido (lo dispara tanto "Subir imagen del producto" del
  // estado vacío como el tile "+" del estado con fotos).
  const fileInput = (
    <input
      ref={addMoreRef}
      id={inputId}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      onChange={(e) => {
        if (e.target.files && e.target.files.length > 0) {
          onAddMore(e.target.files);
          e.target.value = '';
        }
      }}
    />
  );

  // Estilo de Mi marca, el mismo del alta de Mi marca (VariantForm) —
  // ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca.
  const nameInput = (
    <div>
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <label htmlFor={`${inputId}-nombre`} className="text-[13px] font-semibold text-[#16181D]">
          Nombre
        </label>
        <span className="text-[12.5px] text-black/40">Opcional</span>
      </div>
      <input
        id={`${inputId}-nombre`}
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Si lo dejás vacío, lo completa la IA"
        disabled={uploading}
        className="h-11 w-full rounded-full border border-black/[0.08] bg-white px-4 text-[14.5px] text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none transition-shadow placeholder:text-black/35 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.085)] disabled:opacity-50"
      />
    </div>
  );

  // Zona de subida: la tarjeta punteada con el abanico de Mi marca. Tocar abre el selector;
  // soltar también sube (sólo fotos: el `accept` del input no aplica a lo que se suelta).
  const soltar = {
    onDragOver: (e: React.DragEvent) => {
      if (uploading) return;
      e.preventDefault();
      setArrastrando(true);
    },
    onDragLeave: () => setArrastrando(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setArrastrando(false);
      if (uploading) return;
      const fotos = Array.from(e.dataTransfer.files ?? []).filter(esFoto);
      if (fotos.length > 0) onAddMore(fotos);
    },
  };
  const zonaDeSubida = (
    <label
      htmlFor={inputId}
      {...soltar}
      {...abrirConTeclado(inputId, uploading)}
      className={`group relative flex cursor-pointer flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border-[1.5px] border-dashed px-5 py-8 text-center outline-none transition-[border-color,box-shadow,background-color] duration-300 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ${
        uploading ? 'pointer-events-none opacity-60' : ''
      } ${
        arrastrando
          ? 'border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.085)] shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)]'
          : 'border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)]'
      }`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
      />
      <AbanicoDeFotos />
      <span className="relative">
        <span className="block text-[15px] font-semibold leading-tight text-[#16181D]">
          Subí las fotos del producto
        </span>
        <span className="mt-1 block text-[13px] leading-snug text-black/45">
          <span className="sm:hidden">Tocá para elegirlas</span>
          <span className="hidden sm:inline">Hacé clic o arrastralas acá</span>
          {' · podés sumar varias'}
        </span>
      </span>
    </label>
  );

  // "¿Tenés varios productos?": otro camino, pegado abajo de la zona.
  const filaDeVarios = onBulkUpload ? (
    <button
      type="button"
      onClick={onBulkUpload}
      disabled={uploading}
      className="group flex w-full items-center gap-3 rounded-2xl border border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-white px-3.5 py-3 text-left transition-[border-color,box-shadow] duration-300 hover:border-brand-primary hover:shadow-[0_10px_24px_-14px_rgba(22,68,235,0.45)] disabled:opacity-40"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)]">
        <Layers className="h-4 w-4 text-brand-primary" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold leading-tight text-[#16181D]">
          ¿Tenés varios productos?
        </span>
        <span className="mt-0.5 block text-[12.5px] leading-snug text-black/50">
          Subilos juntos: cada foto es un producto
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-brand-primary transition-transform group-hover:translate-x-0.5" />
    </button>
  ) : null;

  // ── Estado vacío: explicación de cómo subir (ejemplos + tips). Sin
  // contenedor con borde: las imágenes y la aclaración van sueltas. ──────
  const imagesExplainer = (
    <div>
      <p className="mb-2.5 px-0.5 text-[13px] font-semibold text-[#16181D]">Así funcionan mejor</p>
      <div className="grid grid-cols-2 gap-3">
        {[examples.img1, examples.img2].map((src, i) => (
          <div key={i} className="relative aspect-square overflow-hidden rounded-2xl bg-white">
            <img
              src={src}
              alt="Ejemplo de foto de producto"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
            <span className="absolute bottom-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-success text-white shadow-[0_2px_6px_rgba(16,24,40,0.2)] ring-2 ring-white">
              <Check className="h-3.5 w-3.5" strokeWidth={2.75} />
            </span>
          </div>
        ))}
      </div>
      <ul className="mt-3 space-y-1.5 px-0.5 text-[13px] leading-snug text-black/55">
        <li className="flex gap-2">
          <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-success" strokeWidth={2.5} />
          Que se vea claramente el producto que querés vender.
        </li>
        {isFashion && (
          <li className="flex gap-2">
            <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-success" strokeWidth={2.5} />
            Las fotos del producto puesto (sin mostrar la cara) funcionan mejor.
          </li>
        )}
      </ul>
    </div>
  );

  // ── Estado con fotos: grilla de previews + tile "Agregar" ──────────────
  const grid = (
    <div>
      <div className="mb-2.5 flex items-baseline justify-between px-0.5">
        <p className="text-[13px] font-semibold text-[#16181D]">Fotos</p>
        <span className="text-[12.5px] font-medium tabular-nums text-black/40">{files.length}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {files.map((file, idx) => (
          <div
            key={`${file.name}-${idx}`}
            className="relative aspect-square overflow-hidden rounded-2xl bg-black/[0.04] motion-safe:animate-[aparecer_0.45s_ease-out_both]"
          >
            {previews[idx] && (
              <img
                src={previews[idx]}
                alt={file.name}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
            {idx === 0 && (
              <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                Principal
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemoveFile(idx)}
              disabled={uploading}
              aria-label="Quitar foto"
              title="Quitar foto"
              className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-black/70 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all hover:bg-white hover:text-error active:scale-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {/* Una foto más (mismo producto, otro ángulo): la tarjeta punteada de Mi marca. */}
        <TarjetaDeAgregarFoto htmlFor={inputId} cargando={uploading} textoCargando="Subiendo…" {...soltar} />
      </div>
      {isFashion && (
        <p className="mt-3 px-0.5 text-[13px] leading-snug text-black/50">
          Sumá un detalle o la parte de atrás para que Atlas la recree mejor.
        </p>
      )}
    </div>
  );

  const errorBlock = error ? (
    <div className="rounded-2xl bg-error-soft px-3.5 py-3 text-[13px] text-error-text">
      {error}
    </div>
  ) : null;

  const confirmButton = (
    <button
      type="button"
      onClick={onConfirm}
      disabled={uploading || files.length === 0}
      className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-full text-[15px] font-semibold transition-colors ${
        uploading || files.length === 0
          ? 'cursor-not-allowed bg-black/[0.05] text-black/30'
          : 'bg-brand-primary text-white shadow-[0_6px_16px_-6px_rgb(var(--brand-primary-rgb)_/_0.55)] hover:bg-brand-primary-hover'
      }`}
    >
      {uploading ? 'Subiendo…' : 'Confirmar'}
    </button>
  );

  // El cuerpo: en el alta, primero las fotos (lo único obligatorio) y después el nombre.
  const cuerpo = hasFiles ? (
    <>
      {grid}
      {nameInput}
    </>
  ) : (
    <>
      <div className="space-y-3">
        {zonaDeSubida}
        {filaDeVarios}
      </div>
      {imagesExplainer}
      {nameInput}
    </>
  );

  const headerSubtitle = hasFiles
    ? 'Sumá fotos del mismo producto desde distintos ángulos'
    : 'Subí sus fotos y empezá a crear con él';
  const volver = !hideBack ? (
    <button
      type="button"
      onClick={onCancel}
      disabled={uploading}
      className="mb-3 inline-flex items-center gap-1 rounded-full bg-black/[0.05] py-1 pl-2 pr-3 text-[13px] font-medium text-black/60 transition-colors hover:bg-black/[0.09] hover:text-[#16181D] disabled:opacity-40"
    >
      <ChevronLeft className="h-3.5 w-3.5" />
      Volver
    </button>
  ) : null;
  const titulo = (
    <>
      <p className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[#16181D]">
        Agregar producto
      </p>
      <p className="mt-1 text-[13.5px] leading-snug text-black/45">{headerSubtitle}</p>
    </>
  );

  // ── Ancho: dos columnas + barra de CTA (selector de fuentes, 980) ────
  // ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas. La cabecera
  // (título + bajada) la pone el host, así que acá arranca directo el cuerpo.
  if (ancho) {
    return (
      <>
        {fileInput}
        <ColumnasDeAlta>
          <ColumnaDeTrabajo>
            {nameInput}
            {hasFiles ? (
              <div>
                <div className="mb-2.5 flex items-baseline justify-between px-0.5">
                  <p className="text-[13px] font-semibold text-[#16181D]">Fotos</p>
                  <span className="text-[12.5px] font-medium tabular-nums text-black/40">{files.length}</span>
                </div>
                {/* Cuatro por fila en escritorio: a tres las miniaturas se hacían
                    enormes y una tanda normal ya ocupaba dos filas. */}
                <div className="grid grid-cols-3 gap-2.5 md:grid-cols-4">
                  {files.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="relative aspect-square overflow-hidden rounded-2xl bg-black/[0.04]"
                    >
                      {previews[idx] && (
                        <img
                          src={previews[idx]}
                          alt={file.name}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      )}
                      <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
                      <button
                        type="button"
                        onClick={() => onRemoveFile(idx)}
                        disabled={uploading}
                        aria-label="Quitar foto"
                        className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/95 text-black/70 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all hover:bg-white hover:text-error active:scale-90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addMoreRef.current?.click()}
                    disabled={uploading}
                    className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-2xl border-[1.5px] border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] text-brand-primary transition-[border-color,box-shadow] hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Plus className="h-5 w-5" strokeWidth={2.5} />
                    <span className="px-2 text-center text-[12px] font-semibold leading-tight text-[#16181D]">
                      {addTileLabel}
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <ZonaDeSubida
                alto="alta"
                multiple
                titulo="Subí las fotos de tu producto"
                ayuda="Arrastralas acá o hacé click para elegirlas. Podés sumar varias del mismo producto."
                deshabilitada={uploading}
                onArchivos={onAddMore}
              />
            )}
            {errorBlock}
          </ColumnaDeTrabajo>
          <AsideDeAlta titulo="Cómo tiene que ser la foto">
            <div className="grid grid-cols-2 gap-2.5">
              {[examples.img1, examples.img2].map((src, i) => (
                <div
                  key={i}
                  className="relative aspect-square overflow-hidden rounded-2xl bg-white"
                >
                  <img
                    src={src}
                    alt="Ejemplo de foto de producto"
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
                  <span className="absolute bottom-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-success text-white shadow-[0_2px_6px_rgba(16,24,40,0.2)] ring-2 ring-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.75} />
                  </span>
                </div>
              ))}
            </div>
            <TipsDeAlta
              items={[
                'Que se vea claramente el producto que querés vender.',
                ...(isFashion
                  ? ['Las fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.']
                  : []),
                'Sumá más ángulos del mismo producto para que Atlas lo recree mejor.',
              ]}
            />
          </AsideDeAlta>
        </ColumnasDeAlta>
        <PieDeAlta
          secundario={
            onBulkUpload && !hasFiles ? (
              <BotonSecundarioDeAlta
                onClick={onBulkUpload}
                disabled={uploading}
                title="Subí varias imágenes a la vez; cada una será un producto distinto"
              >
                <Layers className="h-4 w-4 shrink-0" />
                Subir varios productos
              </BotonSecundarioDeAlta>
            ) : null
          }
        >
          <BotonPrincipalDeAlta onClick={onConfirm} disabled={uploading || !hasFiles}>
            {uploading ? 'Subiendo…' : 'Confirmar'}
          </BotonPrincipalDeAlta>
        </PieDeAlta>
      </>
    );
  }

  // ── Desktop: header / scroll / footer fijos ─────────────────────────
  if (isDesktop) {
    return (
      <div className="flex h-full flex-col">
        {fileInput}
        {(!sinEncabezado || volver) && (
          <div className="shrink-0 px-6 pb-4 pt-6">
            {volver}
            {!sinEncabezado && titulo}
          </div>
        )}
        <div className={`min-h-0 flex-1 space-y-6 overflow-y-auto px-6 pb-6 ${sinEncabezado && !volver ? 'pt-2' : ''}`}>
          {cuerpo}
          {errorBlock}
        </div>
        <div className="shrink-0 border-t border-black/[0.06] px-6 pt-3 pb-1 md:flex md:justify-end md:pb-4 md:pt-4">
          <div className="md:w-auto md:min-w-[180px]">{confirmButton}</div>
        </div>
      </div>
    );
  }

  // ── Mobile: inline con padding del wrapper externo (el host define la
  // altura; no forzamos h-full para no colapsar en contenedores auto). ──
  return (
    <div className="flex flex-col gap-6">
      {fileInput}
      {(!sinEncabezado || volver) && (
        <div>
          {volver}
          {!sinEncabezado && titulo}
        </div>
      )}
      {cuerpo}
      {errorBlock}
      {confirmButton}
    </div>
  );
};
